import type { ReactNode } from 'react';
import styled from 'styled-components';

const inputStyles = `
  width: 100%;
  min-width: 0;
  min-height: 44px;
  border-radius: 10px;
  border: 1px solid var(--field-border);
  background: var(--field-bg);
  padding: 11px 14px;
  font-size: 14px;
  color: var(--field-text);
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

  &::placeholder {
    color: var(--field-placeholder);
  }
  &:focus {
    border-color: var(--field-focus);
    box-shadow: 0 0 0 3px var(--field-ring);
  }
  &:disabled {
    background: var(--field-disabled);
    opacity: 0.7;
  }
`;

export const Input = styled.input`
  --field-border: ${({ theme }) => theme.colors.borderStrong};
  --field-focus: ${({ theme }) => theme.colors.primary};
  --field-ring: ${({ theme }) => theme.colors.primarySoft};
  --field-bg: ${({ theme }) => theme.colors.surface};
  --field-text: ${({ theme }) => theme.colors.textPrimary};
  --field-placeholder: ${({ theme }) => theme.colors.textMuted};
  --field-disabled: ${({ theme }) => theme.colors.background};
  ${inputStyles}
`;

export const Textarea = styled.textarea`
  --field-border: ${({ theme }) => theme.colors.borderStrong};
  --field-focus: ${({ theme }) => theme.colors.primary};
  --field-ring: ${({ theme }) => theme.colors.primarySoft};
  --field-bg: ${({ theme }) => theme.colors.surface};
  --field-text: ${({ theme }) => theme.colors.textPrimary};
  --field-placeholder: ${({ theme }) => theme.colors.textMuted};
  --field-disabled: ${({ theme }) => theme.colors.background};
  ${inputStyles}
  min-height: 120px;
  resize: vertical;
  line-height: 1.6;
`;

const FieldWrap = styled.label`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 7px;
`;

const LabelRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;

const LabelText = styled.span`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const HintText = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const HelpText = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

interface FieldProps {
  label: string;
  hint?: string;
  help?: string;
  htmlFor?: string;
  children: ReactNode;
}

export function Field({ label, hint, help, htmlFor, children }: FieldProps) {
  return (
    <FieldWrap htmlFor={htmlFor}>
      <LabelRow>
        <LabelText>{label}</LabelText>
        {hint && <HintText>{hint}</HintText>}
      </LabelRow>
      {children}
      {help && <HelpText>{help}</HelpText>}
    </FieldWrap>
  );
}
